import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, ExternalLink, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { updateNode, getFunnels, getFunnelDetails } from '@/services/api';
import type { FlowNode } from '@/types/flow';
import type { FunnelResponse, NodeResponse } from '@/types/funnel';

interface GoToConfigProps {
    node: FlowNode;
    onClose: () => void;
    currentFunnelPageIds?: string[];
}

export default function GoToConfig({ node, onClose, currentFunnelPageIds = [] }: GoToConfigProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [nodeName, setNodeName] = useState(node.data.node_name || '');
    const [funnelId, setFunnelId] = useState<number | null>(node.data.config.goto_funnel_id || null);
    const [triggerNodeId, setTriggerNodeId] = useState<number | null>(node.data.config.goto_trigger_node_id || null);
    const [allFunnels, setAllFunnels] = useState<FunnelResponse[]>([]);
    const [triggerNodes, setTriggerNodes] = useState<NodeResponse[]>([]);

    useEffect(() => {
        getFunnels(true).then(setAllFunnels).catch(() => {});
    }, []);

    const funnels = allFunnels;

    const selectedFunnelPages = funnelId ? (allFunnels.find(f => f.id === funnelId)?.pages ?? []).map(p => p.page_id) : [];
    const selectedFunnelIncompatible = funnelId && currentFunnelPageIds.length > 0
        && !currentFunnelPageIds.every(pid => selectedFunnelPages.includes(pid));

    useEffect(() => {
        if (!funnelId) {
            setTriggerNodes([]);
            return;
        }
        getFunnelDetails(funnelId)
            .then(detail => setTriggerNodes(detail.nodes.filter(n => n.is_trigger)))
            .catch(() => setTriggerNodes([]));
    }, [funnelId]);

    const updateMutation = useMutation({
        mutationFn: (data: any) => updateNode(node.data.id, data),
        onSuccess: async () => {
            toast({ title: 'Salvo!', description: 'Configuração do Go To atualizada.' });
            await queryClient.invalidateQueries({ queryKey: ['funnel-details'] });
            onClose();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    const handleSave = () => {
        if (!funnelId || !triggerNodeId) {
            toast({
                variant: 'destructive',
                title: 'Selecione um flow',
                description: 'Escolha o flow e o node de entrada.',
            });
            return;
        }

        updateMutation.mutate({
            nome: nodeName,
            goto_funnel_id: funnelId,
            goto_trigger_node_id: triggerNodeId,
        });
    };

    const isSaving = updateMutation.isPending;
    const selectedFunnel = funnels.find(f => f.id === funnelId);

    return (
        <>
            <div className="flex-1 overflow-auto">
                <div className="p-6 space-y-6">
                    <div>
                        <Label htmlFor="node-name">Nome do Node (opcional)</Label>
                        <Input
                            id="node-name"
                            value={nodeName}
                            onChange={(e) => setNodeName(e.target.value)}
                            placeholder="Go To"
                            className="mt-1.5"
                            disabled={isSaving}
                        />
                    </div>

                    <div className="space-y-3 p-4 bg-violet-50 rounded-lg border border-violet-200">
                        <div className="flex items-center gap-2 mb-2">
                            <ExternalLink className="w-4 h-4 text-violet-600" />
                            <p className="text-sm font-medium text-violet-900">Redirecionar para Flow</p>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs text-gray-600">Funil *</Label>
                            <select
                                value={funnelId ?? ''}
                                onChange={e => {
                                    const id = e.target.value ? Number(e.target.value) : null;
                                    setFunnelId(id);
                                    setTriggerNodeId(null);
                                }}
                                disabled={isSaving}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                            >
                                <option value="">Selecione um flow...</option>
                                {funnels.map(f => (
                                    <option key={f.id} value={f.id}>{f.nome}</option>
                                ))}
                            </select>
                        </div>

                        {funnelId && (
                            <div className="space-y-2">
                                <Label className="text-xs text-gray-600">Node de entrada (trigger) *</Label>
                                <select
                                    value={triggerNodeId ?? ''}
                                    onChange={e => setTriggerNodeId(e.target.value ? Number(e.target.value) : null)}
                                    disabled={isSaving || triggerNodes.length === 0}
                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                                >
                                    <option value="">Selecione o node de entrada...</option>
                                    {triggerNodes.map(n => (
                                        <option key={n.id} value={n.id}>
                                            {n.nome || `Node #${n.id}`}
                                            {n.triggers?.[0]?.trigger_value ? ` — "${n.triggers[0].trigger_value}"` : ''}
                                        </option>
                                    ))}
                                </select>
                                {triggerNodes.length === 0 && (
                                    <p className="text-xs text-amber-600">Este funil não possui nodes de trigger.</p>
                                )}
                            </div>
                        )}

                        {selectedFunnelIncompatible && (
                            <div className="flex gap-2 p-3 bg-amber-50 rounded border border-amber-300 mt-2">
                                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                                <p className="text-xs text-amber-800">
                                    O flow selecionado não contém todas as páginas deste flow. O lead só será redirecionado se estiver em uma página compatível.
                                </p>
                            </div>
                        )}

                        {selectedFunnel && triggerNodeId && (
                            <div className="p-3 bg-white rounded border border-violet-200 mt-2">
                                <p className="text-xs text-violet-700">
                                    O lead será redirecionado para <strong>{selectedFunnel.nome}</strong> quando passar por este node.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
                <Button variant="outline" onClick={onClose} disabled={isSaving}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={isSaving || !funnelId || !triggerNodeId}
                >
                    {isSaving ? 'Salvando...' : (
                        <>
                            <Save className="w-4 h-4 mr-2" />
                            Salvar Configurações
                        </>
                    )}
                </Button>
            </div>
        </>
    );
}
